import React, { useState, useEffect } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Toaster,
  Title3,
  Body1,
} from "@fluentui/react-components";
import { DocumentSearchRegular } from "@fluentui/react-icons";
import { Category, Report } from "../../types/reportTypes";
import apiServices from "../../service";
import { useToastMessage } from "../../components/ToastMessage/ToastMessage";
import OverlayLoader from "../../components/OverlayLoader";
import { transformReportConfig } from "../../utils/reportTransform";
import ReportCatalogSidebar from "./components/ReportCatalogSidebar";
import ReportForm from "./components/ReportForm";
import "./style.css";

const useStyles = makeStyles({
  root: { display: "flex", height: "calc(100vh - 150px)" },
  main: {
    flex: "1 1 auto",
    minWidth: 0,
    overflowY: "auto",
    ...shorthands.padding("16px"),
  },
  error: {
    color: "#D13438",
    fontSize: "12px",
    ...shorthands.margin("4px", 0, 0, 0),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: tokens.spacingVerticalS,
    paddingTop: "80px",
    paddingBottom: "80px",
    textAlign: "center",
    height: "calc(100vh - 230px)",
    color: "#f00",
  },
  emptyStateIcon: {
    fontSize: "32px",
    color: "#0f6cbd",
    marginBottom: tokens.spacingVerticalXS,
  },
});

function DynamicReport() {
  const styles = useStyles();
  const { showToast, toasterId } = useToastMessage();
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reportList, setReportList] = useState<any>(null);

  const getReportList = () => {
    setIsLoading(true);
    setError(null);

    apiServices.getReport
      .getList()
      .then((res: any) => {
        if (!res.data.isError) {
          if (res.data) {
            const transformedCategories = transformReportConfig(res.data);
            setCategories(transformedCategories);
          }
        } else {
          setError("Failed to load report list");
        }
        setIsLoading(false);
      })
      .catch((e: Error) => {
        console.error("Error fetching report list:", e);
        setError("An error occurred while fetching the report list");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getReportList();
  }, []);

  const handleSelectReport = (report: Report) => {
    setSelectedReport({
      ...report,
      parameters: report.Metadata?.parameters || [],
    });
  };

  const handleReportGenerated = () => {
    setSelectedReport(null);
  };

  const handleClearSelection = () => {
    setSelectedReport(null);
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div className={styles.root}>
        <OverlayLoader isLoading={isLoading} />
        <ReportCatalogSidebar
          categories={categories}
          selectedReport={selectedReport}
          onSelectReport={handleSelectReport}
        />
        <div className={styles.main}>
          {error && <div className={styles.error}>{error}</div>}

          {!selectedReport && !error && (
            <div className={styles.emptyState}>
              <h3>No report selected</h3>
              <Body1>
                Choose a report from the left-hand panel. Once selected, the
                report parameters will appear here.
              </Body1>
            </div>
          )}

          {selectedReport && (
            <>
              <h3 style={{ padding: "0 0 10px" }}>{selectedReport?.Name}</h3>
              <ReportForm
                selectedReport={selectedReport}
                onReportGenerated={handleReportGenerated}
                onClear={handleClearSelection}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                showToast={(title, message, type) =>
                  showToast(
                    title,
                    message,
                    type as "success" | "error" | "warning",
                  )
                }
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default DynamicReport;
